import { useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { IAttendee, IUIFilters, IWebinarAttendees } from '@src/webinar/types';
import { useRoomStore } from '@src/stores';
import { handleCapacityLimitError } from '@src/utils';

import {
  useFilteredWebinarAttendees,
  usePostWebinarAttendeeAction,
  usePostWebinarAttendeeActionByFilter,
  useWebinarAttendeesQuery,
} from '@src/hooks/queries/useWebinarQuery';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@src/utils/queryKeys';
import { IAttendeesFilters, ICountry, TBasicOption } from '@src/types';

export const useWebinarAttendees = (id?: string) => {
  const queryClient = useQueryClient();
  const { data = {} as IWebinarAttendees, isLoading, isError } = useWebinarAttendeesQuery(id);
  const { mutate: acknowledgeAttendeesByFilter } = usePostWebinarAttendeeActionByFilter();
  const { mutate: acknowledgeAttendees } = usePostWebinarAttendeeAction();

  const tableRef = useRef<any>(null);
  const [selectedAttendees, setSelectedAttendees] = useState<IAttendee[]>([]);
  const [isBulkActionOpen, setIsBulkActionOpen] = useState<boolean>(false);
  const [modalStatus, setModalStatus] = useState<boolean>(true);
  const [isBulkActionLoading, setIsBulkActionLoading] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

  const [filters, setFilters] = useState<IUIFilters>({
    types: [],
    emailDomains: [],
    countries: [],
    companies: [],
    search: '',
  });

  const [draftFilters, setDraftFilters] = useState<IUIFilters>(filters);

  const { setInfoModalContent, setIsInfoModalOpen } = useRoomStore(
    useShallow((state) => ({
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
    }))
  );
  const apiFilters: IAttendeesFilters = useMemo(
    () => ({
      type: filters.types.map((t) => t.value),
      domain: filters.emailDomains.map((d) => d.value),
      country: filters.countries.map((c) => c.value),
      company: filters.companies.map((c) => c.value),
      search: filters.search,
    }),
    [filters]
  );
  const SelectAll = () => {
    tableRef.current?.toggleAllRowsSelected(true);
    const allSelectedRows = tableRef.current?.getRowModel().rows.map((row: any) => row.original);
    setSelectedAttendees(allSelectedRows);
  };

  const UnselectAll = () => {
    tableRef?.current?.resetRowSelection();

    setSelectedAttendees([]);
  };

  const handleFilterChange = (key: keyof IUIFilters, value: TBasicOption[] | ICountry[]) => {
    setFilters((prev: IUIFilters) => ({ ...prev, [key]: value }));
    UnselectAll();
  };

  const handleOpenBulkActionModal = (isModalOpen: boolean, modalStatus: boolean) => {
    setModalStatus(modalStatus);
    setIsBulkActionOpen(isModalOpen);
  };

  const handleAcknowledgeAttendeesByFilter = () => {
    const emailDomainValues = filters.emailDomains.map((item) => item.value).join(',');
    const countryValues = filters.countries.map((item) => item.value).join(',');
    const companyValues = filters.companies.map((item) => item.value).join(',');
    const typesValues = filters.types.map((item) => item.value).join(',');

    setIsBulkActionLoading(true);
    if (selectedAttendees.length < tableRef.current?.getRowModel().rows.length) {
      handleAttendeeAction(modalStatus ? 'APPROVED' : 'REJECTED', selectedAttendees);
    } else {
      acknowledgeAttendeesByFilter(
        {
          roomId: id || '',
          country: countryValues,
          status: { status: modalStatus ? 'APPROVED' : 'REJECTED' },
          searchKeyword: filters.search,
          domain: emailDomainValues,
          company: companyValues,
          type: typesValues,
        },
        {
          onSuccess: () => {
            setIsBulkActionOpen(false);
            setIsBulkActionLoading(false);
            queryClient.removeQueries({ queryKey: [queryKeys.webinar.attendeesPaginated(id ?? '')] });
            queryClient.removeQueries({ queryKey: [queryKeys.webinar.attendees(id ?? '')] });
            UnselectAll();
          },
          onError: (error: any) => {
            setIsBulkActionOpen(false);
            setIsBulkActionLoading(false);
            handleCapacityLimitError({
              error,
              typesValues,
              selectedAttendees,
              setInfoModalContent,
              setIsInfoModalOpen,
            });
          },
        }
      );
    }
  };

  const handleAttendeeAction = (status: 'APPROVED' | 'REJECTED', attendees: IAttendee[]) => {
    acknowledgeAttendees(
      { status, attendees },
      {
        onSuccess: () => {
          setIsBulkActionOpen(false);
          setIsBulkActionLoading(false);
          queryClient.removeQueries({ queryKey: [queryKeys.webinar.attendeesPaginated(id ?? '')] });
          queryClient.removeQueries({ queryKey: [queryKeys.webinar.attendees(id ?? '')] });
          UnselectAll();
        },
        onError: (error: any) => {
          setIsBulkActionOpen(false);
          setIsBulkActionLoading(false);
          handleCapacityLimitError({
            error,
            selectedAttendees,
            setInfoModalContent,
            setIsInfoModalOpen,
          });
        },
      }
    );
  };

  return {
    loading: isLoading,
    error: isError,
    data,
    cardInfoData: data,

    tableRef,
    selectedAttendees,
    setSelectedAttendees,
    SelectAll,
    UnselectAll,
    isBulkActionOpen,
    setIsBulkActionOpen,
    handleOpenBulkActionModal,
    modalStatus,
    filters,
    setFilters,
    handleAcknowledgeAttendeesByFilter,
    isBulkActionLoading,
    isFilterModalOpen,
    setIsFilterModalOpen,
    draftFilters,
    setDraftFilters,
    apiFilters,
    handleFilterChange,
  };
};

export const useFilteredWebinar = (id?: string) => {
  const { data = {} as any } = useFilteredWebinarAttendees(id);

  return {
    filtersOptions: data,
  };
};
